import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {classToClass, plainToClass} from 'class-transformer';
import {SelectItem} from "primeng/api";

import {GrowlService, UserService} from '@app/core';

import {ProjectChargeCode} from '../../classes/charge-code';
import {DefaultChargeCode} from "./charge-code-summary";
import {Format} from '../../enums/format.enum';
import {RefDomainType} from '../../enums/ref-domain-type.enum';
import {Transaction} from '../../enums/txn-type.enum';

import {CommonDataService} from "../../services/common-data.service";
import {ChargeCodeService} from "../../services/charge-code.service";
import {GrowlType, PrimeNgConfig, PrimeNgSupportService, TableColumn, TableFormat} from "@app/shared";


@Component({
  selector: 'app-default-charge-code',
  templateUrl: './default-charge-code.component.html',
  styleUrls: ['./default-charge-code.component.css'],

  providers: [ChargeCodeService]
})
export class DefaultChargeCodeComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() txnType: Transaction;
  @Input() authorizedFrom: string;
  @Input() authorizedThru: string;
  @Input() showDialog: boolean;
  @Output() onCloseDefaultChargeCodeDialog = new EventEmitter<boolean>();

  PrimeNgConfig = PrimeNgConfig;

  headerText: string;
  subTypeDropdownLabel: string;

  defaultChargeCodes: DefaultChargeCode[];

  displayChargeCodeDialog: boolean;
  chargeCodeToEdit: DefaultChargeCode;
  selectedChargeCode: DefaultChargeCode;                             // this is used to update the charge code is not saved
  selectedSubType: number;
  totalAllocation: number;
  availableAllocation: number;

  subTypeList: SelectItem[];
  tableColumns: TableColumn[];
  @ViewChild('actionTemplate') actionTemplate: TemplateRef<any>;

  formErrors = {'selectedSubType': ''};

  validationMessages = {
    'selectedSubType': {
      'required': 'Selection is required.'
    }
  };

  constructor(private chargeCodeService: ChargeCodeService, private commonDataService: CommonDataService, private userService: UserService, private growl: GrowlService, private datePipe: DatePipe,
              private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.initTableColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {

    this.subTypeList = [];
    if (this.txnType === Transaction.PurchaseRequisition) {
      this.headerText = "This dialog allows you to setup default charge codes for each requisition types. Please select requisition to begin.";
      this.subTypeDropdownLabel = "Requisition Type";
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.PurchaseRequisitionType, false).subscribe(res => this.subTypeList = res);
    } else if (this.txnType === Transaction.ExpenseReport) {
      this.subTypeDropdownLabel = "Project Type";
      this.headerText = "This dialog allows you to setup default charge codes for each project types. Please select project type to begin.";
      this.commonDataService.getRefCodeAsDropdown(RefDomainType.ExpenseCategory, false).subscribe(res => this.subTypeList = res);
    }

    if (!this.authorizedFrom) {
      this.authorizedFrom = this.datePipe.transform(new Date(), Format.DataserviceDate);
    }
  }

  onChangeSubType(subTypeId: number) {
    if (subTypeId > 0) {
      this.chargeCodeService.getMyDefaultChargeCodes(this.txnType, subTypeId, this.authorizedFrom, this.authorizedThru).subscribe(res => {
          this.processChargeCodes(plainToClass(DefaultChargeCode, res.list));
          setTimeout(() => this.initTableColumns(), 0);
        }
      );
    } else {
      this.defaultChargeCodes = [];
    }
  }

  onClickAddNewChargeCode() {
    this.selectedChargeCode = null;
    this.chargeCodeToEdit = new DefaultChargeCode();
    this.chargeCodeToEdit.transaction = this.txnType;
    this.chargeCodeToEdit.subTypeId = this.selectedSubType;
    this.availableAllocation = this.calculateAvailableAllocation();
    this.chargeCodeToEdit.allocationPercent = this.availableAllocation;
    this.displayChargeCodeDialog = true;
  }

  onClickEditChargeCode(chargeCode: DefaultChargeCode) {
    this.selectedChargeCode = chargeCode;
    this.chargeCodeToEdit = classToClass(chargeCode);
    this.availableAllocation = this.calculateAvailableAllocation(this.chargeCodeToEdit.allocationPercent);
    this.displayChargeCodeDialog = true;
  }

  handleSelectedChargeCode(selectedChargeCode: ProjectChargeCode) {
    this.chargeCodeToEdit.chargeCodeId = selectedChargeCode.id;
    this.chargeCodeToEdit.chargeNumber = selectedChargeCode.chargeNumber;
  }

  onClickAddOrUpdateChargeCode(chargeCodeToEdit: DefaultChargeCode, selectedChargeCode: DefaultChargeCode) {
    if (this.selectedChargeCode) {
      this.defaultChargeCodes = this.primeNgService.replaceByObj(this.defaultChargeCodes, chargeCodeToEdit, selectedChargeCode);
    } else if (!chargeCodeToEdit.id) {
      this.defaultChargeCodes = this.primeNgService.push(this.defaultChargeCodes, chargeCodeToEdit);
    }
    this.totalAllocation = this.calculateTotalAllocation(this.defaultChargeCodes);
    this.displayChargeCodeDialog = false;
  }

  onClickDeleteChargeCode(chargeCodeToEdit: DefaultChargeCode) {
    this.defaultChargeCodes = this.primeNgService.deleteById(this.defaultChargeCodes, chargeCodeToEdit.id);
    this.totalAllocation -= chargeCodeToEdit.allocationPercent;
    this.displayChargeCodeDialog = false;
  }

  onClickSaveDefaultChargeCodes(defaultChargeCodes: DefaultChargeCode[]) {
    if (defaultChargeCodes && defaultChargeCodes.length > 0) {
      this.chargeCodeService.saveMyDefaultChargeCodes(defaultChargeCodes).subscribe(res => {
          this.processChargeCodes(plainToClass(DefaultChargeCode, res.list));
          this.onClickHideMainDialog();
          this.growl.add(GrowlType.Success, "Default charge codes updated for selected " + this.subTypeDropdownLabel + ".");
        }
      );
    } else {
      this.deleteAllDefaultChargeCodes();
    }
  }

  deleteAllDefaultChargeCodes() {
    this.chargeCodeService.deleteAllMyDefaultChargeCodes(this.txnType, this.selectedSubType).subscribe(res => {
        this.onCloseDefaultChargeCodeDialog.emit(false);
        this.showDialog = false;
        this.growl.add(GrowlType.Success, 'Default charge code updated.');
      }
    );
  }

  onClickHideMainDialog() {
    this.onCloseDefaultChargeCodeDialog.emit(false);
    this.defaultChargeCodes = null;
    this.showDialog = false;
  }

  onClickHideChargeCodeDialog() {
    this.displayChargeCodeDialog = false;
  }

  private initTableColumns(): void {
    this.tableColumns = [
      {field: 'chargeNumber', header: 'Charge Code'},
      {field: 'allocationPercent', header: 'Allocation (%)', format: TableFormat.PostFix, postFix: "%"},
      {field: 'action', header: 'Action', template: this.actionTemplate}
    ];
  }

  private calculateTotalAllocation(defaultChargeCodes: DefaultChargeCode[]): number {
    let total = 0;
    defaultChargeCodes.forEach(cc => total += cc.allocationPercent);
    return total;
  }

  private calculateAvailableAllocation(currentAllocation?: number): number {
    return currentAllocation ? currentAllocation + 100 - this.totalAllocation : 100 - this.totalAllocation;
  }

  private processChargeCodes(defaultChargeCodes: DefaultChargeCode[]) {
    this.defaultChargeCodes = defaultChargeCodes;
    this.totalAllocation = this.calculateTotalAllocation(defaultChargeCodes);
  }
}
